/**
 * SMSWiser shortlink options carried per drop / draft / preset item.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Mirrors SMSWiser's API enums (POST /drops, PATCH /drops/{id}) and their
 * API defaults, so a drop that never touched these options sends exactly
 * what it sent before they existed. One source for backend validation and
 * every frontend picker.
 */

export const SHORTLINK_CLICK_ID_STRUCTURES = ['path', 'subdomain'] as const;
export type ShortlinkClickIdStructure =
  (typeof SHORTLINK_CLICK_ID_STRUCTURES)[number];

export const SHORTLINK_DOMAIN_SCHEMES = ['none', 'https'] as const;
export type ShortlinkDomainScheme = (typeof SHORTLINK_DOMAIN_SCHEMES)[number];

export const DEFAULT_CLICK_ID_STRUCTURE: ShortlinkClickIdStructure = 'path';
export const DEFAULT_DOMAIN_SCHEME: ShortlinkDomainScheme = 'none';

export interface ShortlinkOptionMeta<T extends string> {
  value: T;
  label: string;
  /** What the resulting shortlink looks like — shown under the picker. */
  example: string;
}

export const CLICK_ID_STRUCTURE_OPTIONS: ReadonlyArray<
  ShortlinkOptionMeta<ShortlinkClickIdStructure>
> = [
  { value: 'path', label: 'Path', example: 'domain.com/abc123' },
  { value: 'subdomain', label: 'Subdomain', example: 'abc123.domain.com' },
];

export const DOMAIN_SCHEME_OPTIONS: ReadonlyArray<
  ShortlinkOptionMeta<ShortlinkDomainScheme>
> = [
  { value: 'none', label: 'None', example: 'domain.com/…' },
  { value: 'https', label: 'Https', example: 'https://domain.com/…' },
];

export function isShortlinkClickIdStructure(
  v: unknown,
): v is ShortlinkClickIdStructure {
  return (
    typeof v === 'string' &&
    (SHORTLINK_CLICK_ID_STRUCTURES as readonly string[]).includes(v)
  );
}

export function isShortlinkDomainScheme(
  v: unknown,
): v is ShortlinkDomainScheme {
  return (
    typeof v === 'string' &&
    (SHORTLINK_DOMAIN_SCHEMES as readonly string[]).includes(v)
  );
}

/** Normalize anything stored/received to a valid value (unknown → default). */
export function toClickIdStructure(v: unknown): ShortlinkClickIdStructure {
  return isShortlinkClickIdStructure(v) ? v : DEFAULT_CLICK_ID_STRUCTURE;
}

export function toDomainScheme(v: unknown): ShortlinkDomainScheme {
  return isShortlinkDomainScheme(v) ? v : DEFAULT_DOMAIN_SCHEME;
}
